import React, { createContext, useContext } from "react";
import type { ToastProps, ToastType } from "./Toast";

// Native stub for ToastProvider.
//
// Toast on B2B is a web-only floating overlay (uses createPortal, DOM
// positioning, mouse events). Mobile/RN should use platform-native feedback
// patterns (RN's Animated.View overlay, Snackbar, etc.). This stub keeps the
// public API surface available so shared code can call `useToast()` without
// crashing — every action is a no-op.

export type ToastPosition =
  | "top-right"
  | "top-left"
  | "top-center"
  | "bottom-right"
  | "bottom-left"
  | "bottom-center";

export type ToastVariant = "default" | "sonner";

export interface ToastOptions extends Omit<
  ToastProps,
  "type" | "onClose" | "testID"
> {
  duration?: number;
  id?: string;
  onDismiss?: () => void;
}

export interface ToastQueueItem extends ToastOptions {
  id: string;
  type: ToastType;
  createdAt: number;
}

export interface ToastApi {
  alert: (options: ToastOptions) => string;
  success: (options: ToastOptions) => string;
  warning: (options: ToastOptions) => string;
  info: (options: ToastOptions) => string;
  show: (type: ToastType, options: ToastOptions) => string;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

export interface ToastProviderProps {
  children: React.ReactNode;
  position?: ToastPosition;
  limit?: number;
  container?: unknown;
  animated?: boolean;
  animationDuration?: number;
  variant?: ToastVariant;
  testID?: string;
}

const noop = () => {};
const noopWithId = (_options: ToastOptions): string => "";

const NATIVE_API: ToastApi = {
  alert: noopWithId,
  success: noopWithId,
  warning: noopWithId,
  info: noopWithId,
  show: (_type, _options) => "",
  dismiss: noop,
  dismissAll: noop,
};

const ToastContext = createContext<ToastApi>(NATIVE_API);

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => (
  <ToastContext.Provider value={NATIVE_API}>{children}</ToastContext.Provider>
);

ToastProvider.displayName = "ToastProvider";

export const useToast = (): ToastApi => useContext(ToastContext);
